import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Star,
  Phone,
  MessageCircle,
  Calendar,
  Shield,
  BadgeCheck,
  Flag,
  ChevronLeft,
  Share2,
  Heart,
  Eye,
  TrendingUp,
  Building2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useListing,
  useVendor,
  useListingReviews,
  useRecordClick,
  useReportListing,
} from "@/hooks/useForex";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "@/lib/utils";

const ListingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const {
    listing,
    isLoading: isLoadingListing,
    recordView,
  } = useListing(id || "");
  const { vendor } = useVendor(listing?.vendorId || "");
  const {
    reviews,
    isLoading: isLoadingReviews,
    submitReview,
  } = useListingReviews(id || "");
  const { recordClick } = useRecordClick();
  const { reportListing } = useReportListing();

  // Record view when page loads
  useEffect(() => {
    if (id) {
      recordView();
    }
  }, [id, recordView]);

  const handleContact = (type: "call" | "whatsapp") => {
    if (id) {
      recordClick(id);
    }

    if (type === "call" && vendor?.phone) {
      window.open(`tel:${vendor.phone}`, "_blank");
    } else if (type === "whatsapp" && vendor?.whatsapp) {
      const message = encodeURIComponent(
        `Hi, I saw your listing on Boruda Exchange and I'm interested in exchanging currency.`,
      );
      window.open(
        `https://wa.me/${vendor.whatsapp.replace(/\+/g, "")}?text=${message}`,
        "_blank",
      );
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: listing?.title,
        text: `Check out this exchange vendor on Boruda Exchange: ${listing?.title}`,
        url: window.location.href,
      });
    } catch {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied",
        description: "Listing link copied to clipboard",
      });
    }
  };

  const handleReport = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    reportListing({
      listingId: id || "",
      vendorId: listing?.vendorId || "",
      reporterName: formData.get("name") as string,
      reporterPhone: formData.get("phone") as string,
      reason: formData.get("reason") as string,
      details: formData.get("details") as string,
    });

    setReportDialogOpen(false);
    toast({
      title: "Report submitted",
      description:
        "Thank you for your report. We will investigate this listing.",
    });
  };

  const handleSubmitReview = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    submitReview({
      listingId: id || "",
      vendorId: listing?.vendorId || "",
      reviewerName: formData.get("name") as string,
      rating: parseInt(formData.get("rating") as string),
      comment: formData.get("comment") as string,
    });

    setReviewDialogOpen(false);
    toast({
      title: "Review submitted",
      description: "Thank you for your feedback!",
    });
  };

  if (isLoadingListing) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4">
          <Skeleton className="h-96 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Skeleton className="h-64" />
            </div>
            <div>
              <Skeleton className="h-64" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold mb-2">Listing Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The listing you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/forex")}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Forex Market
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Image */}
      <div className="relative h-64 md:h-96">
        <img
          src={listing.images[0] || "/placeholder-shop.jpg"}
          alt={listing.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Navigation */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => navigate("/forex/listings")}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setIsFavorite(!isFavorite)}
            >
              <Heart
                className={`w-5 h-5 ${isFavorite ? "fill-red-500 text-red-500" : ""}`}
              />
            </Button>
            <Button variant="secondary" size="icon" onClick={handleShare}>
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 mb-2">
            {listing.isFeatured && (
              <Badge className="bg-primary">
                <Star className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
            {vendor?.isVerified && (
              <Badge variant="secondary">
                <BadgeCheck className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            {listing.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="rates" className="w-full">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="rates">Exchange Rates</TabsTrigger>
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="reviews">
                  Reviews ({reviews.length})
                </TabsTrigger>
              </TabsList>

              {/* Rates Tab */}
              <TabsContent value="rates" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Current Exchange Rates
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {listing.currencies.map((curr) => (
                        <div
                          key={`${curr.from}-${curr.to}`}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{curr.from}</span>
                              <span className="text-muted-foreground">→</span>
                              <span className="font-semibold">{curr.to}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                We Buy
                              </div>
                              <div className="font-bold text-green-600">
                                ₦{curr.buyRate.toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">
                                We Sell
                              </div>
                              <div className="font-bold text-primary">
                                ₦{curr.sellRate.toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                            Min: ₦{curr.minAmount.toLocaleString()} | Max: ₦
                            {curr.maxAmount.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* About Tab */}
              <TabsContent value="about" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>About This Vendor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Description</h4>
                      <p className="text-muted-foreground">
                        {listing.description}
                      </p>
                    </div>

                    {vendor && (
                      <>
                        <div>
                          <h4 className="font-semibold mb-2">Business Hours</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(vendor.businessHours).map(
                              ([day, hours]) => (
                                <div key={day} className="flex justify-between">
                                  <span className="capitalize text-muted-foreground">
                                    {day}:
                                  </span>
                                  <span>{hours}</span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold mb-2">Location</h4>
                          <div className="flex items-start gap-2 text-muted-foreground">
                            <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div>
                              <p>{listing.location.address}</p>
                              <p>
                                {listing.location.city},{" "}
                                {listing.location.state}
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {listing.views} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Listed{" "}
                        {formatDistanceToNow(new Date(listing.createdAt))}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="mt-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Customer Reviews</CardTitle>
                    <Dialog
                      open={reviewDialogOpen}
                      onOpenChange={setReviewDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button size="sm">Write a Review</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Write a Review</DialogTitle>
                        </DialogHeader>
                        <form
                          onSubmit={handleSubmitReview}
                          className="space-y-4 mt-4"
                        >
                          <div>
                            <Label htmlFor="name">Your Name</Label>
                            <Input id="name" name="name" required />
                          </div>
                          <div>
                            <Label htmlFor="rating">Rating</Label>
                            <select
                              id="rating"
                              name="rating"
                              className="w-full h-10 px-3 rounded-md border border-input bg-background"
                              required
                            >
                              <option value="5">5 - Excellent</option>
                              <option value="4">4 - Very Good</option>
                              <option value="3">3 - Good</option>
                              <option value="2">2 - Fair</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="comment">Your Review</Label>
                            <Textarea id="comment" name="comment" required />
                          </div>
                          <Button type="submit" className="w-full">
                            Submit Review
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    {isLoadingReviews ? (
                      <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <Skeleton key={i} className="h-24" />
                        ))}
                      </div>
                    ) : reviews.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">
                          No reviews yet. Be the first to review!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <div
                            key={review.id}
                            className="border-b last:border-0 pb-4 last:pb-0"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                  <span className="font-semibold text-sm">
                                    {review.reviewerName.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold">
                                    {review.reviewerName}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className={`w-4 h-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-muted"}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {formatDistanceToNow(
                                  new Date(review.createdAt),
                                )}
                              </span>
                            </div>
                            <p className="text-muted-foreground ml-13">
                              {review.comment}
                            </p>
                            {review.isVerified && (
                              <Badge variant="outline" className="mt-2 ml-13">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Verified Customer
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Contact Card */}
          <div>
            <div className="sticky top-24 space-y-4">
              {/* Vendor Info Card */}
              <Card>
                <CardContent className="p-6">
                  {vendor && (
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">
                          {vendor.shopName}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="ml-1 font-medium">
                              {vendor.rating}
                            </span>
                          </div>
                          <span className="text-muted-foreground">
                            ({vendor.reviewCount} reviews)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={() => handleContact("call")}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call Vendor
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      size="lg"
                      onClick={() => handleContact("whatsapp")}
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </div>

                  {/* Safety Notice */}
                  <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">
                          Safety Tip
                        </p>
                        <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                          Always meet in public places and verify the vendor's
                          identity before transacting.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Card */}
              <Card>
                <CardContent className="p-4">
                  <Dialog
                    open={reportDialogOpen}
                    onOpenChange={setReportDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full text-muted-foreground"
                      >
                        <Flag className="w-4 h-4 mr-2" />
                        Report this listing
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Report Listing</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleReport} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="report-name">Your Name</Label>
                          <Input id="report-name" name="name" required />
                        </div>
                        <div>
                          <Label htmlFor="report-phone">Your Phone</Label>
                          <Input id="report-phone" name="phone" required />
                        </div>
                        <div>
                          <Label htmlFor="report-reason">Reason</Label>
                          <select
                            id="report-reason"
                            name="reason"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background"
                            required
                          >
                            <option value="">Select a reason</option>
                            <option value="Suspicious activity">
                              Suspicious activity
                            </option>
                            <option value="False advertising">
                              False advertising
                            </option>
                            <option value="Unethical behavior">
                              Unethical behavior
                            </option>
                            <option value="Scam or fraud">Scam or fraud</option>
                            <option value="Wrong location">
                              Wrong location
                            </option>
                            <option value="Closed business">
                              Closed business
                            </option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="report-details">Details</Label>
                          <Textarea
                            id="report-details"
                            name="details"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          variant="destructive"
                          className="w-full"
                        >
                          Submit Report
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailPage;
